import { getSupabaseClient, isSupabaseConfigured } from '../lib/supabase';
import {
  DEFAULT_EMAIL_FROM_EMAIL,
  DEFAULT_EMAIL_MESSAGE_STREAM,
  DEFAULT_EMAIL_REPLY_TO,
  getDefaultEmailFlowPresets,
  getDefaultEmailTemplatePresets,
} from '../../shared/emailTemplatePresets.js';

export type EmailFlowTrigger =
  | 'payment_paid'
  | 'payment_pending'
  | 'checkout_abandoned'
  | 'payment_failed';

export type EmailFlowRecipient = 'customer' | 'admin';

export interface EmailTemplate {
  id?: string;
  templateKey: string;
  name: string;
  alias: string;
  subject: string;
  htmlBody: string;
  textBody: string;
  messageStream: string;
  fromEmail?: string;
  replyTo?: string;
  active: boolean;
  postmarkTemplateId?: number;
  sampleModel: Record<string, unknown>;
  lastSyncedAt?: string;
}

export interface EmailFlow {
  id?: string;
  flowKey: string;
  name: string;
  description: string;
  triggerType: EmailFlowTrigger;
  recipientType: EmailFlowRecipient;
  templateAlias: string;
  messageStream: string;
  delayMinutes: number;
  maxSends: number;
  enabled: boolean;
}

export interface EmailHistoryEntry {
  id: string;
  source: string;
  status: string;
  to: string;
  templateAlias: string;
  messageStream: string;
  tag: string;
  subject: string;
  messageId: string;
  submittedAt: string;
  error: string;
}

interface EmailTemplateRow {
  id?: string;
  template_key: string;
  name: string;
  alias: string;
  subject: string;
  html_body: string;
  text_body: string;
  message_stream: string;
  from_email: string | null;
  reply_to: string | null;
  active: boolean | null;
  postmark_template_id: number | null;
  sample_model: unknown;
  last_synced_at: string | null;
}

interface EmailFlowRow {
  id?: string;
  flow_key: string;
  name: string;
  description: string | null;
  trigger_type: EmailFlowTrigger;
  recipient_type: EmailFlowRecipient;
  template_alias: string;
  message_stream: string;
  delay_minutes: number | null;
  max_sends: number | null;
  enabled: boolean | null;
}

const TEMPLATES_STORAGE_KEY = 'csd_email_templates';
const FLOWS_STORAGE_KEY = 'csd_email_flows';
const EMAIL_AUTOMATION_URL = '/emailAutomation.php';

const defaultTemplates: EmailTemplate[] = getDefaultEmailTemplatePresets({
  messageStream: DEFAULT_EMAIL_MESSAGE_STREAM,
  fromEmail: DEFAULT_EMAIL_FROM_EMAIL,
  replyTo: DEFAULT_EMAIL_REPLY_TO,
}) as EmailTemplate[];

const defaultFlows: EmailFlow[] = getDefaultEmailFlowPresets({
  messageStream: DEFAULT_EMAIL_MESSAGE_STREAM,
}) as EmailFlow[];

const safeObject = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const mapTemplateRow = (row: EmailTemplateRow): EmailTemplate => ({
  id: row.id,
  templateKey: row.template_key,
  name: row.name,
  alias: row.alias,
  subject: row.subject,
  htmlBody: row.html_body,
  textBody: row.text_body,
  messageStream: row.message_stream,
  fromEmail: row.from_email || '',
  replyTo: row.reply_to || '',
  active: Boolean(row.active),
  postmarkTemplateId: row.postmark_template_id || undefined,
  sampleModel: safeObject(row.sample_model),
  lastSyncedAt: row.last_synced_at || '',
});

const mapFlowRow = (row: EmailFlowRow): EmailFlow => ({
  id: row.id,
  flowKey: row.flow_key,
  name: row.name,
  description: row.description || '',
  triggerType: row.trigger_type,
  recipientType: row.recipient_type,
  templateAlias: row.template_alias,
  messageStream: row.message_stream,
  delayMinutes: Number(row.delay_minutes || 0),
  maxSends: Number(row.max_sends || 1),
  enabled: Boolean(row.enabled),
});

const saveLocal = <T,>(key: string, value: T) => {
  localStorage.setItem(key, JSON.stringify(value));
};

const readLocal = <T,>(key: string, fallbackValue: T): T => {
  try {
    const rawValue = localStorage.getItem(key);
    if (!rawValue) {
      return fallbackValue;
    }

    return JSON.parse(rawValue) as T;
  } catch {
    return fallbackValue;
  }
};

const parseServerResponse = async <T,>(response: Response): Promise<T> => {
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || 'Erreur email automation');
  }

  return payload as T;
};

type EmailAutomationStore = {
  templates: EmailTemplate[];
  flows: EmailFlow[];
};

async function loadServerStore(): Promise<EmailAutomationStore> {
  const response = await fetch(EMAIL_AUTOMATION_URL, {
    credentials: 'include',
    cache: 'no-store',
  });

  const payload = await parseServerResponse<Partial<EmailAutomationStore>>(response);

  return {
    templates: Array.isArray(payload.templates) ? payload.templates : [],
    flows: Array.isArray(payload.flows) ? payload.flows : [],
  };
}

async function saveServerStore(store: Partial<EmailAutomationStore>): Promise<EmailAutomationStore> {
  const response = await fetch(EMAIL_AUTOMATION_URL, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(store),
  });

  const payload = await parseServerResponse<{ ok: true; store: EmailAutomationStore }>(response);
  return payload.store;
}

async function ensureDefaults(): Promise<void> {
  if (!isSupabaseConfigured()) {
    try {
      const serverStore = await loadServerStore();
      const templateKeys = new Set(
        serverStore.templates
          .map((template) => template?.templateKey)
          .filter((templateKey): templateKey is string => typeof templateKey === 'string')
      );
      const flowKeys = new Set(
        serverStore.flows
          .map((flow) => flow?.flowKey)
          .filter((flowKey): flowKey is string => typeof flowKey === 'string')
      );

      const missingTemplates = defaultTemplates.filter(
        (template) => !templateKeys.has(template.templateKey)
      );
      const missingFlows = defaultFlows.filter((flow) => !flowKeys.has(flow.flowKey));

      const nextTemplates =
        serverStore.templates.length > 0
          ? [...serverStore.templates, ...missingTemplates]
          : defaultTemplates;
      const nextFlows =
        serverStore.flows.length > 0 ? [...serverStore.flows, ...missingFlows] : defaultFlows;

      if (missingTemplates.length > 0 || missingFlows.length > 0) {
        await saveServerStore({
          templates: nextTemplates,
          flows: nextFlows,
        });
      }

      saveLocal(TEMPLATES_STORAGE_KEY, nextTemplates);
      saveLocal(FLOWS_STORAGE_KEY, nextFlows);
      return;
    } catch (error) {
      console.error('Fallback email automation storage used:', error);

      const existingTemplates = readLocal<EmailTemplate[]>(TEMPLATES_STORAGE_KEY, []);
      const existingFlows = readLocal<EmailFlow[]>(FLOWS_STORAGE_KEY, []);

      if (existingTemplates.length === 0) {
        saveLocal(TEMPLATES_STORAGE_KEY, defaultTemplates);
      }

      if (existingFlows.length === 0) {
        saveLocal(FLOWS_STORAGE_KEY, defaultFlows);
      }

      return;
    }
  }

  const supabase = getSupabaseClient();
  const [
    { data: existingTemplates, error: templatesReadError },
    { data: existingFlows, error: flowsReadError },
  ] = await Promise.all([
    supabase.from('csd_email_templates').select('template_key'),
    supabase.from('csd_email_flows').select('flow_key'),
  ]);

  if (templatesReadError) {
    throw templatesReadError;
  }

  if (flowsReadError) {
    throw flowsReadError;
  }

  const existingTemplateKeys = new Set(
    ((existingTemplates || []) as Array<{ template_key: string }>).map(
      (entry) => entry.template_key
    )
  );
  const existingFlowKeys = new Set(
    ((existingFlows || []) as Array<{ flow_key: string }>).map((entry) => entry.flow_key)
  );

  const missingTemplates = defaultTemplates
    .filter((template) => !existingTemplateKeys.has(template.templateKey))
    .map((template) => ({
      template_key: template.templateKey,
      name: template.name,
      alias: template.alias,
      subject: template.subject,
      html_body: template.htmlBody,
      text_body: template.textBody,
      message_stream: template.messageStream,
      from_email: template.fromEmail || null,
      reply_to: template.replyTo || null,
      active: template.active,
      sample_model: template.sampleModel,
    }));

  const missingFlows = defaultFlows
    .filter((flow) => !existingFlowKeys.has(flow.flowKey))
    .map((flow) => ({
      flow_key: flow.flowKey,
      name: flow.name,
      description: flow.description,
      trigger_type: flow.triggerType,
      recipient_type: flow.recipientType,
      template_alias: flow.templateAlias,
      message_stream: flow.messageStream,
      delay_minutes: flow.delayMinutes,
      max_sends: flow.maxSends,
      enabled: flow.enabled,
    }));

  const operations = [];

  if (missingTemplates.length > 0) {
    operations.push(supabase.from('csd_email_templates').insert(missingTemplates));
  }

  if (missingFlows.length > 0) {
    operations.push(supabase.from('csd_email_flows').insert(missingFlows));
  }

  if (operations.length === 0) {
    return;
  }

  const results = await Promise.all(operations);

  for (const result of results) {
    if (result.error) {
      throw result.error;
    }
  }
}

export async function getEmailTemplates(): Promise<EmailTemplate[]> {
  await ensureDefaults();

  if (!isSupabaseConfigured()) {
    try {
      const store = await loadServerStore();
      const templates = store.templates.length > 0 ? store.templates : defaultTemplates;
      saveLocal(TEMPLATES_STORAGE_KEY, templates);
      return templates;
    } catch (error) {
      console.error('Reading email templates from local fallback:', error);
      return readLocal<EmailTemplate[]>(TEMPLATES_STORAGE_KEY, defaultTemplates);
    }
  }

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('csd_email_templates')
    .select('*')
    .order('created_at', { ascending: true });

  if (error) {
    throw error;
  }

  return ((data || []) as EmailTemplateRow[]).map(mapTemplateRow);
}

export async function saveEmailTemplate(template: EmailTemplate): Promise<void> {
  await ensureDefaults();

  if (!isSupabaseConfigured()) {
    try {
      const store = await loadServerStore().catch(() => ({
        templates: readLocal<EmailTemplate[]>(TEMPLATES_STORAGE_KEY, defaultTemplates),
        flows: readLocal<EmailFlow[]>(FLOWS_STORAGE_KEY, defaultFlows),
      }));
      const nextTemplates = store.templates.some((entry) => entry.templateKey === template.templateKey)
        ? store.templates.map((entry) =>
            entry.templateKey === template.templateKey ? template : entry
          )
        : [template, ...store.templates];

      await saveServerStore({
        templates: nextTemplates,
        flows: store.flows,
      });
      saveLocal(TEMPLATES_STORAGE_KEY, nextTemplates);
      return;
    } catch (error) {
      console.error('Saving email template to local fallback:', error);
      const templates = readLocal<EmailTemplate[]>(TEMPLATES_STORAGE_KEY, defaultTemplates);
      const nextTemplates = templates.some((entry) => entry.templateKey === template.templateKey)
        ? templates.map((entry) =>
            entry.templateKey === template.templateKey ? template : entry
          )
        : [template, ...templates];
      saveLocal(TEMPLATES_STORAGE_KEY, nextTemplates);
      return;
    }
  }

  const supabase = getSupabaseClient();
  const { error } = await supabase.from('csd_email_templates').upsert(
    {
      template_key: template.templateKey,
      name: template.name,
      alias: template.alias,
      subject: template.subject,
      html_body: template.htmlBody,
      text_body: template.textBody,
      message_stream: template.messageStream,
      from_email: template.fromEmail || null,
      reply_to: template.replyTo || null,
      active: template.active,
      sample_model: template.sampleModel,
      last_synced_at: template.lastSyncedAt || null,
      postmark_template_id: template.postmarkTemplateId || null,
    },
    { onConflict: 'template_key' }
  );

  if (error) {
    throw error;
  }
}

export async function getEmailFlows(): Promise<EmailFlow[]> {
  await ensureDefaults();

  if (!isSupabaseConfigured()) {
    try {
      const store = await loadServerStore();
      const flows = store.flows.length > 0 ? store.flows : defaultFlows;
      saveLocal(FLOWS_STORAGE_KEY, flows);
      return flows;
    } catch (error) {
      console.error('Reading email flows from local fallback:', error);
      return readLocal<EmailFlow[]>(FLOWS_STORAGE_KEY, defaultFlows);
    }
  }

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('csd_email_flows')
    .select('*')
    .order('created_at', { ascending: true });

  if (error) {
    throw error;
  }

  return ((data || []) as EmailFlowRow[]).map(mapFlowRow);
}

export async function saveEmailFlow(flow: EmailFlow): Promise<void> {
  await ensureDefaults();

  if (!isSupabaseConfigured()) {
    try {
      const store = await loadServerStore().catch(() => ({
        templates: readLocal<EmailTemplate[]>(TEMPLATES_STORAGE_KEY, defaultTemplates),
        flows: readLocal<EmailFlow[]>(FLOWS_STORAGE_KEY, defaultFlows),
      }));
      const nextFlows = store.flows.some((entry) => entry.flowKey === flow.flowKey)
        ? store.flows.map((entry) => (entry.flowKey === flow.flowKey ? flow : entry))
        : [flow, ...store.flows];

      await saveServerStore({
        templates: store.templates,
        flows: nextFlows,
      });
      saveLocal(FLOWS_STORAGE_KEY, nextFlows);
      return;
    } catch (error) {
      console.error('Saving email flow to local fallback:', error);
      const flows = readLocal<EmailFlow[]>(FLOWS_STORAGE_KEY, defaultFlows);
      const nextFlows = flows.some((entry) => entry.flowKey === flow.flowKey)
        ? flows.map((entry) => (entry.flowKey === flow.flowKey ? flow : entry))
        : [flow, ...flows];
      saveLocal(FLOWS_STORAGE_KEY, nextFlows);
      return;
    }
  }

  const supabase = getSupabaseClient();
  const { error } = await supabase.from('csd_email_flows').upsert(
    {
      flow_key: flow.flowKey,
      name: flow.name,
      description: flow.description,
      trigger_type: flow.triggerType,
      recipient_type: flow.recipientType,
      template_alias: flow.templateAlias,
      message_stream: flow.messageStream,
      delay_minutes: flow.delayMinutes,
      max_sends: flow.maxSends,
      enabled: flow.enabled,
    },
    { onConflict: 'flow_key' }
  );

  if (error) {
    throw error;
  }
}

export async function getEmailHistory(limit = 100): Promise<EmailHistoryEntry[]> {
  const response = await fetch(`/emailHistory.php?limit=${encodeURIComponent(String(limit))}`, {
    credentials: 'include',
    cache: 'no-store',
  });

  const payload = await parseServerResponse<{ history?: EmailHistoryEntry[] }>(response);
  return Array.isArray(payload.history) ? payload.history : [];
}

export const emailAutomationService = {
  ensureDefaults,
  getEmailTemplates,
  saveEmailTemplate,
  getEmailFlows,
  saveEmailFlow,
  getEmailHistory,
};
